// Firebase initialization and helper to subscribe to RTDB '/videos'
import { initializeApp } from "firebase/app";
import {
  getDatabase,
  ref,
  onValue,
  off,
  push,
  get,
  child,
  runTransaction,
  update,
} from "firebase/database";

// Your Firebase config (from user)
const firebaseConfig = {
  apiKey: "AIzaSyA1tkRiBiSd12avlxTrkkX6NJabQeU912k",
  authDomain: "all-animestamil.firebaseapp.com",
  databaseURL: "https://all-animestamil-default-rtdb.firebaseio.com",
  projectId: "all-animestamil",
  storageBucket: "all-animestamil.firebasestorage.app",
  messagingSenderId: "714889787176",
  appId: "1:714889787176:web:b9a53cd11bf2ae638b021d",
  measurementId: "G-8M0ZMDB6S3",
};

const app = initializeApp(firebaseConfig);
const db = getDatabase(app);

// In-memory caches to reduce RTDB reads
let videosCache = null; // raw object from /videos
let videoIdToKeyMap = {}; // map from videoId (or derived id) -> push-key

// Queues for batching increments to reduce transaction calls
const viewsQueue = {}; // key -> accumulated delta
const likesQueue = {}; // key -> accumulated delta
const flushTimers = {}; // key -> timer id for debounced flush

const FLUSH_DELAY_MS = 5000; // wait up to 5s to coalesce updates

// Subscribe to '/videos' path. callback receives the raw value (object or null).
export const subscribeToVideos = (callback) => {
  const videosRef = ref(db, "videos");
  const listener = (snapshot) => {
    const val = snapshot.val();
    // update in-memory cache and key map whenever RTDB pushes an update
    videosCache = val || null;
    videoIdToKeyMap = buildKeyMapFromVideos(videosCache);
    callback(val);
  };
  onValue(videosRef, listener);
  // return unsubscribe
  return () => off(videosRef, "value", listener);
};

// Load videos once without real-time updates
export const loadVideosOnce = async () => {
  const videosRef = ref(db, "videos");
  const snapshot = await get(videosRef);
  const val = snapshot.val();
  // populate in-memory cache and key map for subsequent lookups
  videosCache = val || null;
  videoIdToKeyMap = buildKeyMapFromVideos(videosCache);
  return val;
};

// Build a map of videoId -> push-key from the raw /videos object
const buildKeyMapFromVideos = (videosObj) => {
  const map = {};
  if (!videosObj) return map;
  const keys = Object.keys(videosObj);
  for (const k of keys) {
    const item = videosObj[k];
    const possibleId = item?.videoId || item?.video?.videoId || null;
    if (possibleId) {
      map[String(possibleId)] = k;
      continue;
    }

    // attempt to derive ID from common URL fields
    const url = item?.url || item?.video?.url || item?.videoUrl || item?.youtubeUrl;
    if (url) {
      try {
        const u = new URL(url);
        // youtube short links
        if (u.hostname.includes("youtu.be")) {
          const id = u.pathname.slice(1);
          if (id) map[String(id)] = k;
        }
        // standard youtube links with v param
        if (u.searchParams && u.searchParams.has("v")) {
          map[String(u.searchParams.get("v"))] = k;
        }
      } catch (e) {
        // ignore malformed URL
      }
    }
  }
  return map;
};

export default app;

// Add a video entry to /videos
export const addVideo = async (videoObj) => {
  const videosRef = ref(db, "videos");
  const newRef = push(videosRef);
  await update(newRef, videoObj);
  return newRef.key;
};

// find the push-key for a video with given videoId
const findVideoKeyByVideoId = async (videoId) => {
  // Fast path: if we have a cached map from previous load, use it
  if (videoIdToKeyMap && videoIdToKeyMap[String(videoId)]) {
    return videoIdToKeyMap[String(videoId)];
  }

  // If cache not available, do a one-time read and populate the cache
  try {
    const snapshot = await get(child(ref(db), "videos"));
    const val = snapshot.val();
    if (!val) return null;
    // populate cache for future
    videosCache = val;
    videoIdToKeyMap = buildKeyMapFromVideos(videosCache);
    if (videoIdToKeyMap[String(videoId)]) return videoIdToKeyMap[String(videoId)];

    // as a final fallback, scan manually (rare)
    const keys = Object.keys(val);
    for (const k of keys) {
      const item = val[k];
      if (item?.videoId === videoId || item?.video?.videoId === videoId) return k;
      const url = item?.url || item?.video?.url || item?.videoUrl || item?.youtubeUrl;
      if (url) {
        try {
          const u = new URL(url);
          if (u.hostname.includes("youtu.be")) {
            const id = u.pathname.slice(1);
            if (id === videoId) return k;
          }
          if (u.searchParams.has("v") && u.searchParams.get("v") === videoId) return k;
          const parts = u.pathname.split("/");
          const embedIdx = parts.indexOf("embed");
          if (embedIdx !== -1 && parts.length > embedIdx + 1 && parts[embedIdx + 1] === videoId) return k;
        } catch (e) {
          // ignore malformed URL
        }
      }
    }
  } catch (e) {
    console.warn('findVideoKeyByVideoId fallback failed', e);
  }
  return null;
};

// increment views atomically for a video (creates stats if missing)
export const incrementView = async (videoId) => {
  const key = await findVideoKeyByVideoId(videoId);
  if (!key) return null;
  // accumulate locally and flush after debounce to reduce DB transactions
  viewsQueue[key] = (viewsQueue[key] || 0) + 1;
  scheduleFlushForKey(key, 'views');
  return key;
};

// change likes by delta (+1 or -1) atomically
export const changeLikes = async (videoId, delta) => {
  const key = await findVideoKeyByVideoId(videoId);
  if (!key) return null;
  likesQueue[key] = (likesQueue[key] || 0) + delta;
  scheduleFlushForKey(key, 'likes');
  return key;
};

// schedule a debounced flush for a particular key and type ('views'|'likes')
const scheduleFlushForKey = (key, type) => {
  const timerId = flushTimers[`${type}:${key}`];
  if (timerId) return; // already scheduled
  flushTimers[`${type}:${key}`] = setTimeout(async () => {
    try {
      if (type === 'views' && viewsQueue[key]) {
        const delta = viewsQueue[key];
        const viewsRef = ref(db, `videos/${key}/stats/views`);
        // apply accumulated delta in a single transaction
        await runTransaction(viewsRef, (current) => {
          return (current || 0) + delta;
        });
        delete viewsQueue[key];
      }
      if (type === 'likes' && likesQueue[key]) {
        const delta = likesQueue[key];
        const likesRef = ref(db, `videos/${key}/stats/likes`);
        await runTransaction(likesRef, (current) => {
          return Math.max(0, (current || 0) + delta);
        });
        delete likesQueue[key];
      }
    } catch (e) {
      console.error('Error flushing queued updates', e);
    } finally {
      clearTimeout(flushTimers[`${type}:${key}`]);
      delete flushTimers[`${type}:${key}`];
    }
  }, FLUSH_DELAY_MS);
};
